/**
 * Two schedules of one name, told apart (2026-10-30). Pure.
 *
 * A timing edit to a schedule with old runs still to come before the
 * change day keeps those runs on a SECOND schedule (api-server 0.6.54,
 * `scheduler.CarryOver`): the old time and task, the same name, ending
 * the night before the new pattern's first day. Until it ends the lists
 * show two "Vacuum · L4-Ballroom", and a task either one fired names it
 * only by that name (`calendar:<name>`). The rows of one plan are linked
 * by their ids: a carry-over's is `<root>.<n>`, the edited row keeps the
 * root (`schedules.py::schedule_family_root`, `_family`), and a carry of
 * a carry stays under the same root. The server passes a rename on to
 * every row still to run, so everything here reads the rows alone:
 *
 *  - `scheduleSpanText`: "Until Tue 03/Nov" and "From Wed 04/Nov",
 *    beside each in the lists, so the pair reads as one plan in two
 *    parts ("Sat 31/Oct only" for a row of one day). Rows are never
 *    merged: each is paused, skipped and edited through its own id.
 *  - `samePlan` / `planRoot`: whether two rows are one plan.
 *  - `scheduleOfItem` / `scheduleNamed`: which of them a fired task
 *    came from -- by the fire ledger's id, else by name and time.
 *  - `keptOldTime` / `newTimeAfter`: the rows of the plan still to run
 *    before and after this one, so pausing, resuming or deleting either
 *    says the other is there (`planPartners`).
 *  - `takenOverBy`: the row that carries on after one that has ended.
 *
 * Every day and clock is on the SCHEDULE's zone, as its rule is.
 */
import type { TaskSchedule } from '../../../api/v1';
import { describeRule } from '../rrule';
import type { LaneItem } from './lane-items';
import { formatZonedClock, formatZonedDate, zonedParts, zonedWeekday } from '../zoned-time';
import { dateKeyOf, WEEKDAY_SHORT } from './lane-time';

const MIN = 60_000;

type Span = Pick<TaskSchedule, 'dtstart_ms' | 'until_ms' | 'timezone'>;

/** "Tue 03/Nov"; "Fri 01/Jan/2027" when it is not this year. */
export function dayText(ms: number, timeZone: string, nowMs: number): string {
  const date = formatZonedDate(ms, timeZone);
  const sameYear = zonedParts(ms, timeZone).year === zonedParts(nowMs, timeZone).year;
  return `${WEEKDAY_SHORT[zonedWeekday(ms, timeZone)]} ${sameYear ? date.replace(/\/\d{4}$/, '') : date}`;
}

/**
 * When a schedule's end falls, as words: "until Tue 03/Nov" for an end
 * in the last minute of a day (the editor's 23:59:59 and the server's
 * carry-over, midnight less a millisecond), else "before Wed 04/Nov
 * 09:00". The server cuts a carry-over mid-day when a dragged run's own
 * time is where the change starts, and "until Wed 04/Nov" would then
 * promise that day's run, which it does not keep.
 */
export function endText(untilMs: number, timeZone: string, nowMs: number): string {
  if (endsWithDay(untilMs, timeZone)) return `until ${dayText(untilMs, timeZone, nowMs)}`;
  const stop = stopMs(untilMs);
  return `before ${dayText(stop, timeZone, nowMs)} ${formatZonedClock(stop, timeZone)}`;
}

/** An end in the last minute of a day: the whole day runs. */
function endsWithDay(untilMs: number, timeZone: string): boolean {
  const { hour, minute } = zonedParts(untilMs, timeZone);
  return hour === 23 && minute === 59;
}

/** The moment an end stops, on the minute: 08:59:59.999 reads as 09:00. */
function stopMs(untilMs: number): number {
  return Math.ceil((untilMs + 1) / MIN) * MIN;
}

function upperFirst(s: string): string {
  return s ? s[0]!.toUpperCase() + s.slice(1) : s;
}

/**
 * "From Wed 04/Nov" for a schedule that starts on a later day, "Until
 * Tue 03/Nov" for one with an end still to come, both when both ("Sat
 * 31/Oct only" when that is one day); null for a schedule already
 * running with no end. What tells a carry-over and the schedule it came
 * from apart in a list.
 */
export function scheduleSpanText(s: Span, nowMs: number, siteTimeZone: string): string | null {
  const tz = s.timezone || siteTimeZone;
  const later = dateKeyOf(s.dtstart_ms, tz) > dateKeyOf(nowMs, tz);
  const ends = s.until_ms !== null && s.until_ms !== undefined && s.until_ms >= nowMs;
  // One day to run, still to come: "Sat 31/Oct only" -- a carry-over
  // that keeps one day's old time -- not "from Sat 31/Oct until Sat
  // 31/Oct". Cut mid-day, the time it stops too.
  if (later && ends && dateKeyOf(s.until_ms!, tz) === dateKeyOf(s.dtstart_ms, tz)) {
    const day = `${dayText(s.dtstart_ms, tz, nowMs)} only`;
    return endsWithDay(s.until_ms!, tz)
      ? day
      : `${day}, before ${formatZonedClock(stopMs(s.until_ms!), tz)}`;
  }
  const from = later ? `from ${dayText(s.dtstart_ms, tz, nowMs)}` : null;
  const until = ends ? endText(s.until_ms!, tz, nowMs) : null;
  const text = [from, until].filter((p): p is string => p !== null).join(' ');
  return text ? upperFirst(text) : null;
}

/** `scheduleSpanText` after a comma in a line ("Every day at 09:00,
 *  until Tue 03/Nov"): lower-case, unless it starts with the day. */
export function spanInline(span: string): string {
  return /^(From|Until|Before) /.test(span) ? span[0]!.toLowerCase() + span.slice(1) : span;
}

/** The pattern beside a span ("Every day at 09:00"), without how often
 *  it repeats when the span is one day -- "At 07:30, Wed 04/Nov only",
 *  not "Every day at 07:30, Wed 04/Nov only". */
export function patternBeside(pattern: string, span: string | null): string {
  if (!span || !/ only(,|$)/.test(span)) return pattern;
  return pattern.replace(/^Every .*? at /, 'At ');
}

/**
 * The line above a row's Runs line in its panel and sheet: "Repeats:
 * Every day at 09:00" -- or, for a row of one day, "Time: 07:30", the
 * time the lists show ("At 07:30, Wed 04/Nov only"): "Every day" above
 * "Wed 04/Nov only" reads as every day. With no span the whole rule
 * (`rule`, with its end) says it.
 */
export function repeatsLine(
  pattern: string,
  rule: string,
  span: string | null,
): { k: 'Repeats' | 'Time'; v: string } {
  if (!span) return { k: 'Repeats', v: rule };
  const beside = patternBeside(pattern, span);
  return beside !== pattern && beside.startsWith('At ')
    ? { k: 'Time', v: beside.slice('At '.length) }
    : { k: 'Repeats', v: pattern };
}

/**
 * What a row still has to run, for naming one row of a plan among
 * others of the same name: "every day at 14:00, until Tue 03/Nov",
 * "every day at 15:00, from Wed 04/Nov", "at 09:00, Sat 31/Oct only".
 * The pattern without its end date, which the span says.
 */
export function stillToRunText(
  s: Pick<TaskSchedule, 'rrule' | 'dtstart_ms' | 'until_ms' | 'timezone'>,
  nowMs: number,
  siteTimeZone: string,
): string {
  const tz = s.timezone || siteTimeZone;
  const span = scheduleSpanText(s, nowMs, siteTimeZone);
  const pattern = patternBeside(
    describeRule(s.rrule, s.dtstart_ms, null, tz, { siteTimeZone }),
    span,
  );
  const text = span ? `${pattern}, ${spanInline(span)}` : pattern;
  return text[0]!.toLowerCase() + text.slice(1);
}

/** `"Vacuum · L4-Lobby" (every day at 14:00, until Tue 03/Nov)`: one
 *  row of a plan, told apart from the others of its name. */
export function planRowLabel(
  s: Pick<TaskSchedule, 'name' | 'rrule' | 'dtstart_ms' | 'until_ms' | 'timezone'>,
  nowMs: number,
  siteTimeZone: string,
): string {
  return `"${s.name}" (${stillToRunText(s, nowMs, siteTimeZone)})`;
}

function notEnded(s: Pick<TaskSchedule, 'until_ms'>, nowMs: number): boolean {
  return s.until_ms === null || s.until_ms === undefined || s.until_ms >= nowMs;
}

/** Whether a schedule's end has passed: it will not run again. */
export function hasEnded(s: Pick<TaskSchedule, 'until_ms'>, nowMs: number): boolean {
  return !notEnded(s, nowMs);
}

/** The plan a row belongs to: its id up to the first ".". A
 *  carry-over's id is `<root>.<n>` (api-server 0.6.54), and a carry of a
 *  carry stays under the same root, never `.1.1`. */
export function planRoot(scheduleId: string): string {
  const dot = scheduleId.indexOf('.');
  return dot < 0 ? scheduleId : scheduleId.slice(0, dot);
}

type PlanKey = Pick<TaskSchedule, 'schedule_id'>;

/** Two rows of one plan -- split off one schedule by timing edits --
 *  as the server pairs them: by the root of their ids. Two schedules
 *  made separately are never one plan, whoever made them and when. */
export function samePlan(a: PlanKey, b: PlanKey): boolean {
  return planRoot(a.schedule_id) === planRoot(b.schedule_id);
}

type Named = Pick<
  TaskSchedule,
  'schedule_id' | 'name' | 'dtstart_ms' | 'until_ms' | 'created_at_ms' | 'last_run_ms'
>;

/**
 * The schedule behind a lane item: an occurrence's own; for a task a
 * schedule fired, the one the fire ledger says fired it
 * (`LaneItem.scheduleId`) while that row is still listed, whatever it is
 * called now; else the one its requester names (`scheduleNamed`), the
 * name it had when it fired. Undefined for anything else.
 */
export function scheduleOfItem<T extends Named>(
  item: Pick<LaneItem, 'occurrence' | 'scheduleId' | 'source' | 'startMs'>,
  schedules: readonly T[],
  nowMs: number,
): T | undefined {
  if (item.occurrence) {
    return schedules.find((s) => s.schedule_id === item.occurrence!.schedule_id);
  }
  const fired = item.scheduleId
    ? schedules.find((s) => s.schedule_id === item.scheduleId)
    : undefined;
  if (fired) return fired;
  return item.source?.kind === 'calendar'
    ? scheduleNamed(schedules, item.source.who, item.startMs, nowMs)
    : undefined;
}

/**
 * The schedule a fired task came from, when the task names it only by
 * name (`calendar:<name>`) and the fire ledger in hand does not say
 * (`scheduleOfItem` asks it first). With one of that name, it. With two
 * or more (the rows of one plan, or two that happen to share a name),
 * never one made after the task started; then by PLAN -- the rows that
 * share a root id. A plan held the task when it had begun by then (a
 * row starting on or before it, or one with a run on record: a retime
 * moves a row's start onto its first NEW run, so a start still to come
 * says nothing of its past) and its last row had not ended. Of those,
 * one with a row still to run, then the newest. Two plans made
 * separately that both held it can still be confused here; only the
 * ledger tells them apart. Within the plan, the row whose own span
 * holds the start; else the row that keeps the plan's root id -- the
 * continuing one, which made every run before the first edit; else the
 * later start.
 */
export function scheduleNamed<T extends Named>(
  schedules: readonly T[],
  name: string,
  atMs: number,
  nowMs: number,
): T | undefined {
  const named = schedules.filter((s) => s.name === name);
  if (named.length <= 1) return named[0];
  const made = named.filter((s) => s.created_at_ms <= atMs);
  const plans = new Map<string, T[]>();
  for (const s of made.length > 0 ? made : named) {
    const root = planRoot(s.schedule_id);
    plans.set(root, [...(plans.get(root) ?? []), s]);
  }
  const end = (s: T) => s.until_ms ?? Infinity;
  const held = (rows: readonly T[]) =>
    rows.some((s) => s.dtstart_ms <= atMs || (s.last_run_ms ?? null) !== null) &&
    rows.some((s) => atMs <= end(s));
  const live = (rows: readonly T[]) => Number(rows.some((s) => notEnded(s, nowMs)));
  const newest = (rows: readonly T[]) => Math.max(...rows.map((s) => s.created_at_ms));
  const all = [...plans.values()];
  const holding = all.filter(held);
  const plan = (holding.length > 0 ? holding : all).sort(
    (a, b) => live(b) - live(a) || newest(b) - newest(a),
  )[0]!;
  const spans = (s: T) => Number(s.dtstart_ms <= atMs && atMs <= end(s));
  const root = (s: T) => Number(planRoot(s.schedule_id) === s.schedule_id);
  return [...plan].sort(
    (a, b) => spans(b) - spans(a) || root(b) - root(a) || b.dtstart_ms - a.dtstart_ms,
  )[0];
}

type Row = Pick<TaskSchedule, 'schedule_id' | 'name' | 'dtstart_ms' | 'until_ms'>;

/** Another row of `s`'s plan. A rename reaches every row still to run,
 *  so the rows these look for carry its name too. */
function partnerOf(s: Row, o: Row): boolean {
  return o.schedule_id !== s.schedule_id && samePlan(o, s);
}

/**
 * The rows of `s`'s plan still to run that end before `s` starts: the
 * old time an edit kept on a carry-over. Pausing or deleting `s` leaves
 * them running, so the confirmation says so.
 */
export function keptOldTime<T extends Row>(s: T, all: readonly T[], nowMs: number): T[] {
  return all.filter(
    (o) =>
      partnerOf(s, o) &&
      o.until_ms !== null &&
      o.until_ms !== undefined &&
      o.until_ms >= nowMs &&
      o.until_ms < s.dtstart_ms,
  );
}

/**
 * The rows of `s`'s plan that start after `s` ends and have not ended:
 * for a carry-over, the schedule that takes over at the new time. Until
 * the change day every run on the lanes is the carry-over's, so this is
 * the one a Pause there would leave running.
 */
export function newTimeAfter<T extends Row>(s: T, all: readonly T[], nowMs: number): T[] {
  const until = s.until_ms;
  if (until === null || until === undefined) return [];
  return all.filter((o) => partnerOf(s, o) && o.dtstart_ms > until && notEnded(o, nowMs));
}

/**
 * The row that carries on after `s` -- of its plan, not ended, starting
 * after `s` ends, the earliest such -- for actions on a run of a row
 * that has ended. By plan alone: a row that has ended does not take a
 * later rename. Undefined when there is none.
 */
export function takenOverBy<
  T extends Pick<TaskSchedule, 'schedule_id' | 'dtstart_ms' | 'until_ms'>,
>(s: T, all: readonly T[], nowMs: number): T | undefined {
  const until = s.until_ms;
  if (until === null || until === undefined) return undefined;
  return all
    .filter(
      (o) =>
        o.schedule_id !== s.schedule_id &&
        samePlan(o, s) &&
        o.dtstart_ms > until &&
        notEnded(o, nowMs),
    )
    .sort((a, b) => a.dtstart_ms - b.dtstart_ms)[0];
}

/** `Another "Vacuum · L4-Ballroom" keeps the old time until Tue
 *  03/Nov.` -- or, for more than one, the last of their ends. Paused
 *  (`paused`), for the question Resume asks: `Another "...", the old
 *  time until Tue 03/Nov, is paused too.` */
export function keptOldTimeText(
  name: string,
  kept: readonly Span[],
  nowMs: number,
  siteTimeZone: string,
  paused = false,
): string {
  const last = kept.reduce((a, b) => ((b.until_ms ?? 0) > (a.until_ms ?? 0) ? b : a));
  const end = endText(last.until_ms!, last.timezone || siteTimeZone, nowMs);
  if (paused) {
    return kept.length === 1
      ? `Another "${name}", the old time ${end}, is paused too.`
      : `${kept.length} other "${name}" schedules with the old times, the last ${end}, are paused too.`;
  }
  return kept.length === 1
    ? `Another "${name}" keeps the old time ${end}.`
    : `${kept.length} other "${name}" schedules keep their old times, the last ${end}.`;
}

/** `Another "Vacuum · L4-Ballroom" takes over at 12:00 from Wed
 *  04/Nov.` -- or, for more than one, the first of them. Its start is
 *  its first run at the new time, on its own clock. Paused: `...,
 *  taking over at 12:00 from Wed 04/Nov, is paused too.` */
export function newTimeAfterText(
  name: string,
  later: readonly Span[],
  nowMs: number,
  siteTimeZone: string,
  paused = false,
): string {
  const first = later.reduce((a, b) => (b.dtstart_ms < a.dtstart_ms ? b : a));
  const tz = first.timezone || siteTimeZone;
  const from = dayText(first.dtstart_ms, tz, nowMs);
  const at = formatZonedClock(first.dtstart_ms, tz);
  if (paused) {
    return later.length === 1
      ? `Another "${name}", taking over at ${at} from ${from}, is paused too.`
      : `${later.length} other "${name}" schedules that follow it, the first from ${from}, are paused too.`;
  }
  return later.length === 1
    ? `Another "${name}" takes over at ${at} from ${from}.`
    : `${later.length} other "${name}" schedules follow it, the first from ${from}.`;
}

/**
 * One other row of a plan in a list of them, named as the delete dialog
 * names it: "Another: every day at 09:00, from Sat 31/Oct until Tue
 * 03/Nov". `short` is the phone's: a daily row by its time alone
 * ("Another: 09:00, from Sat 31/Oct until Tue 03/Nov"); any other
 * pattern is kept, since the days are then what tells two rows apart.
 */
export function anotherRowLine(
  s: Pick<TaskSchedule, 'rrule' | 'dtstart_ms' | 'until_ms' | 'timezone'>,
  nowMs: number,
  siteTimeZone: string,
  short = false,
): string {
  const text = stillToRunText(s, nowMs, siteTimeZone);
  return `Another: ${short ? text.replace(/^every day at /, '') : text}`;
}

/** What Pause or Resume on `s` asks about (`KeptPauseConfirm`): the
 *  rows of its plan before and after it, in the state it is in -- a
 *  Pause leaves running ones running, a Resume leaves paused ones
 *  paused. Null when there are none. With one, `text` says it in a
 *  sentence; with more, `text` leads into `list`, a line for each, the
 *  earliest first (`listShort` on a phone). */
export interface PlanPartners<T> {
  rows: T[];
  text: string;
  list: string[];
  listShort: string[];
  verb: 'pause' | 'resume';
}

export function planPartners<T extends Row & Pick<TaskSchedule, 'enabled' | 'timezone' | 'rrule'>>(
  s: T,
  all: readonly T[],
  nowMs: number,
  siteTimeZone: string,
): PlanPartners<T> | null {
  const sameState = (o: T) => o.enabled === s.enabled;
  const earlier = keptOldTime(s, all, nowMs).filter(sameState);
  const later = newTimeAfter(s, all, nowMs).filter(sameState);
  const rows = [...earlier, ...later];
  if (rows.length === 0) return null;
  const paused = !s.enabled;
  const verb = paused ? 'resume' : 'pause';
  if (rows.length === 1) {
    const text = earlier.length
      ? keptOldTimeText(s.name, earlier, nowMs, siteTimeZone, paused)
      : newTimeAfterText(s.name, later, nowMs, siteTimeZone, paused);
    return { rows, text, list: [], listShort: [], verb };
  }
  // More than one: each by what it still has to run. "2 other ... keep
  // their old times, the last until Wed 04/Nov" left the reader to
  // guess which were which, and when.
  const byStart = [...rows].sort((a, b) => a.dtstart_ms - b.dtstart_ms);
  return {
    rows,
    text: paused
      ? `${rows.length} other "${s.name}" schedules are paused too:`
      : `${rows.length} other "${s.name}" schedules are still to run:`,
    list: byStart.map((o) => anotherRowLine(o, nowMs, siteTimeZone)),
    listShort: byStart.map((o) => anotherRowLine(o, nowMs, siteTimeZone, true)),
    verb,
  };
}
